import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2, Sparkles, MapPin, Search, Loader2, ArrowRight, Star, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pill = ({ children, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
      active ? 'bg-[#2d2a26] text-[#c8a97e] border-[#2d2a26]' : 'bg-white text-[#5c5750] border-[#e5dfd0] hover:bg-[#faf6ec]'
    }`}
  >
    {children}
  </button>
);

const CompanyCard = ({ company }) => {
  const tags = (company.expertise_tags || []).slice(0, 3);
  return (
    <Link to={`/ecosystem/c/${company.slug}`} data-testid={`eco-card-${company.slug}`}>
      <Card className="h-full overflow-hidden border-[#e5dfd0] hover:border-[#c8a97e] hover:shadow-lg transition-all group">
        <div
          className="h-24 bg-gradient-to-br from-[#e8dcc4] to-[#c8a97e] relative"
          style={company.banner_url ? { backgroundImage: `url(${company.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
        >
          {company.is_featured && (
            <Badge className="absolute top-2 right-2 bg-[#2d2a26] text-[#c8a97e] border-0 text-[10px] tracking-wider">
              <Sparkles className="w-3 h-3 mr-1" /> FEATURED
            </Badge>
          )}
        </div>
        <CardContent className="p-5 -mt-8 relative">
          <div className="w-14 h-14 rounded-xl bg-white border-2 border-[#e5dfd0] flex items-center justify-center overflow-hidden mb-3 shadow-sm">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.company_name} className="w-full h-full object-cover" />
            ) : (
              <Building2 className="w-6 h-6 text-[#c8a97e]" />
            )}
          </div>
          <div className="flex items-center gap-1.5 mb-1">
            <h3 className="font-heading font-bold text-base text-[#2d2a26] truncate">{company.company_name}</h3>
            {company.is_verified && <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />}
          </div>
          <p className="text-xs text-[#7a7268] mb-2">{company.industry}</p>
          <p className="text-sm text-[#5c5750] line-clamp-2 mb-3">{company.services_summary || company.about}</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(company.categories || []).slice(0, 2).map((c) => (
              <Badge key={c.id} variant="outline" className="text-[10px] border-[#e5dfd0]">{c.name}</Badge>
            ))}
            {tags.map((t) => (
              <Badge key={t} className="text-[10px] bg-[#faf6ec] text-[#5c5750] border-[#e5dfd0]">#{t}</Badge>
            ))}
          </div>
          <div className="flex items-center justify-between text-xs text-[#7a7268] pt-3 border-t border-[#f0e7d4]">
            <div className="flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{company.primary_venue_name || 'Clayworks'}</span>
            </div>
            <span className="text-[#c8a97e] group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1 font-medium">
              View <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const EcosystemDirectoryPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [venueId, setVenueId] = useState('all');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    axios.get(`${API}/ecosystem/categories`).then((r) => setCategories(r.data || [])).catch(() => {});
    axios.get(`${API}/public/venues`).then((r) => setVenues(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort, limit: 60 };
    if (search.trim()) params.q = search.trim();
    if (activeCat !== 'all') params.category_id = activeCat;
    if (venueId !== 'all') params.venue_id = venueId;
    if (featuredOnly) params.featured_only = true;
    axios
      .get(`${API}/ecosystem/companies`, { params })
      .then((r) => setCompanies(r.data?.items || []))
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));
  }, [search, activeCat, venueId, featuredOnly, sort]);

  const featured = useMemo(() => companies.filter((c) => c.is_featured), [companies]);

  return (
    <div className="min-h-screen bg-[#faf6ec]" data-testid="ecosystem-directory">
      {/* Hero */}
      <section className="bg-[#2d2a26] text-white">
        <div className="max-w-7xl mx-auto px-6 py-14 sm:py-20">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#c8a97e]" />
            <span className="text-[#c8a97e] text-sm tracking-[0.2em] font-medium">CLAYWORKS ECOSYSTEM</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-[1.05] mb-5 max-w-3xl">
            Meet the businesses behind <span className="text-[#c8a97e]">Clayworks</span>.
          </h1>
          <p className="text-base sm:text-lg text-[#cfc8b8] max-w-2xl leading-relaxed mb-8">
            An exclusive B2B network of companies that work, build and grow at Clayworks centres. Discover, connect,
            and let our centre managers broker the right intro for you.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/ecosystem/my-profile')}
              className="bg-[#c8a97e] hover:bg-[#b89a6e] text-[#2d2a26] font-semibold"
              data-testid="claim-profile-btn"
            >
              Claim your company profile
            </Button>
            <Button
              onClick={() => document.getElementById('eco-list')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="border-[#c8a97e] text-[#c8a97e] hover:bg-[#c8a97e]/10"
            >
              Browse directory
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        {/* Featured strip */}
        {!loading && featured.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-[#c8a97e] fill-[#c8a97e]" />
              <h2 className="text-base font-heading font-bold text-[#2d2a26] tracking-wide">FEATURED MEMBERS</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.slice(0, 3).map((c) => <CompanyCard key={c.id} company={c} />)}
            </div>
          </section>
        )}

        {/* Filters */}
        <section id="eco-list" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7a7268]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by company, industry or keyword..."
                className="pl-9 bg-white border-[#e5dfd0]"
                data-testid="eco-search"
              />
            </div>
            <Select value={venueId} onValueChange={setVenueId}>
              <SelectTrigger className="w-full sm:w-56 bg-white border-[#e5dfd0]" data-testid="eco-venue-filter">
                <SelectValue placeholder="Centre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All centres</SelectItem>
                {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-full sm:w-44 bg-white border-[#e5dfd0]" data-testid="eco-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Recent</SelectItem>
                <SelectItem value="featured">Featured first</SelectItem>
                <SelectItem value="alpha">A-Z</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={featuredOnly ? 'default' : 'outline'}
              onClick={() => setFeaturedOnly((f) => !f)}
              className={featuredOnly ? 'bg-[#2d2a26] hover:bg-[#1d1a16] text-[#c8a97e]' : 'border-[#e5dfd0] text-[#5c5750]'}
              data-testid="eco-featured-toggle"
            >
              <Star className="w-4 h-4 mr-1.5" /> Featured
            </Button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            <Pill active={activeCat === 'all'} onClick={() => setActiveCat('all')}>All categories</Pill>
            {categories.map((c) => (
              <Pill key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)}>{c.name}</Pill>
            ))}
          </div>
        </section>

        {/* Grid */}
        <section>
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#c8a97e]" /></div>
          ) : companies.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-[#e5dfd0]">
              <Building2 className="w-12 h-12 mx-auto text-[#c8a97e] mb-3" />
              <h3 className="text-lg font-heading font-bold text-[#2d2a26] mb-1">No companies match your filters.</h3>
              <p className="text-sm text-[#7a7268]">
                Try clearing filters or check back soon — we're adding new members every week.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#7a7268] mb-4">{companies.length} companies</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {companies.map((c) => <CompanyCard key={c.id} company={c} />)}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default EcosystemDirectoryPage;
